import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import AIAssistantModal from '../components/AIAssistantModal';
import API from '../config/api';
import './Drafts.css';

const Drafts = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = parseInt(searchParams.get('project_id') || '0');
  const type = searchParams.get('type') || 'mr';
  const prefillTitle = searchParams.get('title') || '';
  const prefillDescription = searchParams.get('description') || '';

  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState({ project_id: 0, project_name: 'No Project' });
  const [formData, setFormData] = useState({
    draft_title: prefillTitle,
    protocol_description: prefillDescription
  });
  const [steps, setSteps] = useState(['']);
  const [reagents, setReagents] = useState([{ name: '', vendor: '', lot: '', serial: '' }]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [createdProtocolId, setCreatedProtocolId] = useState(null);

  // Debug: Log when showSuccessModal changes
  useEffect(() => {
    console.log('showSuccessModal changed to:', showSuccessModal);
    console.log('createdProtocolId:', createdProtocolId);
    if (showSuccessModal) {
      console.log('SUCCESS MODAL SHOULD BE VISIBLE NOW!');
      // Force a re-render check
      setTimeout(() => {
        const modalElement = document.querySelector('.modal-overlay');
        if (modalElement) {
          console.log('Modal element found in DOM!', modalElement);
        } else {
          console.error('Modal element NOT found in DOM!');
        }
      }, 100);
    }
  }, [showSuccessModal, createdProtocolId]);

  useEffect(() => {
    if (projectId > 0) {
      fetchProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await API.get(`/qurator/projects/${projectId}`);
      if (response.data.success) {
        setProject(response.data.project);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const addStep = () => {
    setSteps([...steps, '']);
  };

  const removeStep = (index) => {
    if (steps.length > 1) {
      const newSteps = steps.filter((_, i) => i !== index);
      setSteps(newSteps);
    } else {
      setErrorMessage('You must have at least one step');
      setShowErrorModal(true);
    }
  };

  const updateStep = (index, value) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
  };

  const addReagent = () => {
    setReagents([...reagents, { name: '', vendor: '', lot: '', serial: '' }]);
  };

  const removeReagent = (index) => {
    if (reagents.length > 1) {
      const newReagents = reagents.filter((_, i) => i !== index);
      setReagents(newReagents);
    } else {
      setErrorMessage('You must have at least one reagent');
      setShowErrorModal(true);
    }
  };

  const updateReagent = (index, field, value) => {
    const newReagents = [...reagents];
    newReagents[index][field] = value;
    setReagents(newReagents);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowErrorModal(false);

    // Validation
    if (formData.draft_title.trim().length < 3) {
      setErrorMessage('Title must be at least 3 characters long');
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    if (formData.protocol_description.trim().length < 10) {
      setErrorMessage('Please provide a more detailed description');
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    const validSteps = steps.filter(s => s.trim());
    if (validSteps.length === 0) {
      setErrorMessage('At least one step is required');
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    const validReagents = reagents.filter(r => r.name.trim());
    if (validReagents.length === 0) {
      setErrorMessage('At least one reagent is required');
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        project_id: projectId > 0 ? projectId : null,
        draft_title: formData.draft_title.trim(),
        protocol_description: formData.protocol_description.trim(),
        steps: validSteps,
        reagents: validReagents.map(r => r.name),
        reagent_vendor: reagents.map(r => r.vendor || ''),
        reagent_lot: reagents.map(r => r.lot || ''),
        reagent_serial: reagents.map(r => r.serial || ''),
        manual_ai: 'Manual',
        type: type
      };

      console.log('Creating protocol with payload:', { ...payload, steps: payload.steps.length, reagents: payload.reagents.length });

      const response = await API.post('/qurator/protocols/create', payload);

      console.log('Full response object:', response);
      console.log('Full response.data:', response.data);
      console.log('Response status:', response.status);

      // Check response structure - handle both direct data and nested data
      const responseData = response.data || response;
      console.log('Using responseData:', responseData);

      if (responseData && responseData.success === true) {
        const protocolId = responseData.protocol_id || responseData.data?.protocol_id;
        console.log('Protocol created successfully! protocolId:', protocolId);
        
        if (!protocolId) {
          console.error('Protocol ID is missing! Response:', responseData);
          throw new Error('Protocol ID not returned from server');
        }
        
        // Set protocol ID first
        setCreatedProtocolId(protocolId);
        
        // Close loading modal
        setLoading(false);
        
        // Show success modal immediately
        console.log('About to set showSuccessModal to true');
        setShowSuccessModal(true);
        console.log('showSuccessModal has been set to true');
      } else {
        console.error('Response does not indicate success:', responseData);
        throw new Error(responseData?.message || responseData?.error || 'Failed to create experiment');
      }
    } catch (error) {
      console.error('Error creating protocol:', error);
      console.error('Error response:', error.response?.data);
      setLoading(false);
      setErrorMessage(error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred while creating the experiment');
      setShowErrorModal(true);
    }
  };

  const handleSkip = () => {
    setShowSuccessModal(false);
    navigate('/projects');
  };

  const handleUpload = () => {
    if (createdProtocolId) {
      setShowSuccessModal(false);
      navigate(`/draft-edit?editid=${createdProtocolId}&success=Protocol created successfully`);
    } else {
      console.error('No protocol ID available for upload');
      setShowSuccessModal(false);
    }
  };

  const handleAIProtocolGenerated = (generatedData) => {
    // Populate the form with generated data
    setFormData({
      draft_title: generatedData.draft_title || '',
      protocol_description: generatedData.protocol_description || ''
    });
    setSteps(generatedData.steps || ['']);
    setReagents(generatedData.reagents || [{ name: '', vendor: '', lot: '', serial: '' }]);
    setShowAIModal(false);
    
    // Scroll to form
    setTimeout(() => {
      const formElement = document.getElementById('draftForm');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <QuratorLayout>
      <div className="draft-section">
        <div className="container-fluid px-4">
          {/* Page Header */}
          <div className="page-header">
            <h4 className="mb-1">Create {type === 'mr' ? 'MR' : 'VR'} Experiment</h4>
            <p className="mb-0">Define each step and reagent for your experiment</p>
            <div className="project-info">
              <p className="project-name">Project: {project.project_name}</p>
            </div>
          </div>

          {/* AI Assistant Card */}
          <div className="ai-assist-card">
            <div className="ai-content">
              <div className="ai-icon">
                <i className="bi bi-robot"></i>
              </div>
              <div className="ai-info">
                <h5>Looking for a faster way?</h5>
                <p>Try our AI assistant to automatically generate steps</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowAIModal(true)}
              className="ai-btn"
            >
              Use AI Assistant
              <i className="bi bi-arrow-right"></i>
            </button>
          </div>

          {/* Main Form */}
          <form id="draftForm" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-section">
              <div className="form-title">
                <i className="bi bi-info-circle"></i>
                <h5>Basic Information</h5>
              </div>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label required-field">Experiment Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.draft_title}
                    onChange={(e) => setFormData({ ...formData, draft_title: e.target.value })}
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label required-field">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.protocol_description}
                    onChange={(e) => setFormData({ ...formData, protocol_description: e.target.value })}
                    required
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Steps and Reagents */}
            <div className="row">
              {/* Steps Section */}
              <div className="col-md-6">
                <div className="form-section">
                  <div className="form-title">
                    <i className="bi bi-list-ol"></i>
                    <h5>Steps</h5>
                    <span className="text-danger">*</span>
                  </div>
                  <div className="input-area" id="stepsArea">
                    {steps.map((step, index) => (
                      <div key={index} className="input-item">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">Step {index + 1}</label>
                          {steps.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              onClick={() => removeStep(index)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={step}
                          onChange={(e) => updateStep(index, e.target.value)}
                          required
                        ></textarea>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="add-btn" onClick={addStep}>
                    <i className="bi bi-plus-lg"></i> Add Step
                  </button>
                </div>
              </div>

              {/* Reagents Section */}
              <div className="col-md-6">
                <div className="form-section">
                  <div className="form-title">
                    <i className="bi bi-flask"></i>
                    <h5>Reagents</h5>
                    <span className="text-danger">*</span>
                  </div>
                  <div className="input-area" id="reagentsArea">
                    {reagents.map((reagent, index) => (
                      <div key={index} className="input-item">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">Reagent {index + 1}</label>
                          {reagents.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              onClick={() => removeReagent(index)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          className="form-control mb-2"
                          placeholder="Enter reagent name"
                          value={reagent.name}
                          onChange={(e) => updateReagent(index, 'name', e.target.value)}
                          required
                        />
                        <div className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label small">Vendor</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Optional"
                              value={reagent.vendor}
                              onChange={(e) => updateReagent(index, 'vendor', e.target.value)}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small">Lot #</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Optional"
                              value={reagent.lot}
                              onChange={(e) => updateReagent(index, 'lot', e.target.value)}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small">Serial #</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Optional"
                              value={reagent.serial}
                              onChange={(e) => updateReagent(index, 'serial', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="add-btn" onClick={addReagent}>
                    <i className="bi bi-plus-lg"></i> Add Reagent
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-end">
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg"></i> Create Experiment
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Loading Modal */}
      {loading && (
        <div className="modal-overlay">
          <div className="modal-custom">
            <div className="modal-body text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="mb-0">Creating Experiment...</h5>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal ? (
        <div 
          className="modal-overlay" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '1rem'
          }}
        >
          <div 
            className="modal-custom" 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              zIndex: 100000,
              position: 'relative'
            }}
          >
            <div className="modal-body text-center">
              <i className="bi bi-check-circle-fill success-icon"></i>
              <h5 className="mb-3">Experiment Created Successfully!</h5>
              <p className="mb-4">Would you like to upload reference materials for this experiment?</p>
              <div className="d-flex justify-content-center gap-3">
                <button type="button" className="btn btn-skip px-4" onClick={handleSkip}>
                  Skip
                </button>
                <button type="button" className="btn btn-confirm px-4" onClick={handleUpload}>
                  Upload Files
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="modal-custom" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body text-center">
              <i className="bi bi-x-circle-fill error-icon"></i>
              <h5 className="mb-3">Error Occurred</h5>
              <p id="errorMessage" className="mb-4">{errorMessage}</p>
              <button
                type="button"
                className="btn btn-confirm px-4"
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage('');
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant Modal */}
      <AIAssistantModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        projectId={projectId}
        prefillTitle={formData.draft_title}
        prefillDescription={formData.protocol_description}
        onProtocolGenerated={handleAIProtocolGenerated}
      />
    </QuratorLayout>
  );
};

export default Drafts;
